import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { omit } from '../../util/common';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { isAnyFilterActive, isMainSearchTypeKeywords, isOriginInUse } from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, LayoutSingleColumn, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getActiveListingTypes,
  getSearchPageDefaultFiltersConfig,
  getSearchPageResourceLocatorStringParams,
  getActiveFiltersFromQuery,
  isSearchDirty,
  getTotalItemsFromPagination,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import FilterModal from './FilterModal';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import {
  buildCanonicalSearchQueryKey,
  createSearchQueryKey,
  loadMoreListings,
} from './SearchPage.duck';
import * as listingVisibility from '../../util/listingVisibility';

import css from './SearchPage.module.css';
import { withViewport } from '../../util/uiHelpers';
import { ListingCategory, ListingFieldConfigKey } from '../../util/enums';
import { addOrUpdateField } from '../../util/searchPageHelpers';
import {
  clearSearchScrollState,
  getSearchRouteKey,
  readSearchScrollState,
  saveSearchScrollState,
} from '../../util/searchScrollState';
import { debugInfiniteScroll } from '../../util/infiniteScrollDebug';

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;
const RESTORE_SCROLL_MAX_TRIES = 120;
const { filterPublicListings } = listingVisibility;
const getDefaultFiltersConfig = (config, listingTypePathParam) => {
  const { defaultFilters: defaultFiltersRaw = [] } = config?.search || {};
  return getSearchPageDefaultFiltersConfig(defaultFiltersRaw, listingTypePathParam);
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isFilterModalOpen: false,
      filterModalInitialQueryParams: null,
    };

    this.pendingScrollRestore = null;
    this.restoreScrollAttempts = 0;
    this.restoreScrollRaf = null;

    this.onOpenFilterModal = this.onOpenFilterModal.bind(this);
    this.onCloseFilterModal = this.onCloseFilterModal.bind(this);
    this.cancelFilterModal = this.cancelFilterModal.bind(this);
    this.applyFilterModal = this.applyFilterModal.bind(this);
    this.handleKeywordSearch = this.handleKeywordSearch.bind(this);
    this.clearKeywordsOnly = this.clearKeywordsOnly.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.saveScrollStateBeforeListingNavigation = this.saveScrollStateBeforeListingNavigation.bind(
      this
    );
    this.tryRestoreSavedScrollPosition = this.tryRestoreSavedScrollPosition.bind(this);
    this.scheduleScrollRestore = this.scheduleScrollRestore.bind(this);
  }

  componentDidMount() {
    this.pendingScrollRestore = readSearchScrollState(this.props.location);
    if (this.pendingScrollRestore) {
      this.restoreScrollAttempts = 0;
      this.scheduleScrollRestore();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search && !this.state.isFilterModalOpen) {
      const urlQueryParams = validUrlQueryParamsFromProps(this.props);
      const currentQueryKey = createSearchQueryKey(this.state.currentQueryParams);
      const urlQueryKey = createSearchQueryKey(urlQueryParams);

      if (currentQueryKey !== urlQueryKey) {
        this.setState({ currentQueryParams: urlQueryParams });
      }
    }

    const listingsCountChanged = prevProps.listings?.length !== this.props.listings?.length;
    const searchProgressChanged = prevProps.searchInProgress !== this.props.searchInProgress;
    const locationChanged =
      prevProps.location.pathname !== this.props.location.pathname ||
      prevProps.location.search !== this.props.location.search;

    if (
      this.pendingScrollRestore &&
      (listingsCountChanged || searchProgressChanged || locationChanged)
    ) {
      this.scheduleScrollRestore();
    }
  }

  componentWillUnmount() {
    if (this.restoreScrollRaf && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.restoreScrollRaf);
      this.restoreScrollRaf = null;
    }
  }

  scrollToSearchTop() {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  saveScrollStateBeforeListingNavigation() {
    if (typeof window === 'undefined') {
      return;
    }

    const validQueryParams = validUrlQueryParamsFromProps(this.props);
    const queryKey = this.getSearchQueryKey(validQueryParams);
    saveSearchScrollState({
      location: this.props.location,
      scrollY: window.scrollY || 0,
      loadedItemsCount: this.props.listings?.length || 0,
      currentPage: this.props.currentPage || 1,
      queryKey,
    });
  }

  scheduleScrollRestore() {
    if (typeof window === 'undefined' || !this.pendingScrollRestore || this.restoreScrollRaf) {
      return;
    }

    this.restoreScrollRaf = window.requestAnimationFrame(() => {
      this.restoreScrollRaf = null;
      this.tryRestoreSavedScrollPosition();
    });
  }

  tryRestoreSavedScrollPosition() {
    if (typeof window === 'undefined' || !this.pendingScrollRestore) {
      return;
    }

    const currentRouteKey = getSearchRouteKey(this.props.location);
    if (currentRouteKey !== this.pendingScrollRestore.routeKey) {
      this.pendingScrollRestore = null;
      this.restoreScrollAttempts = 0;
      return;
    }

    const loadedItemsCount = this.props.listings?.length || 0;
    const targetItemsCount = Number(this.pendingScrollRestore.loadedItemsCount || 0);
    const hasEnoughItemsRendered = loadedItemsCount >= targetItemsCount;
    const shouldRestoreNow =
      (!this.props.searchInProgress && hasEnoughItemsRendered) ||
      this.restoreScrollAttempts >= RESTORE_SCROLL_MAX_TRIES;

    if (shouldRestoreNow) {
      window.scrollTo(0, Number(this.pendingScrollRestore.scrollY || 0));
      clearSearchScrollState(this.props.location);
      this.pendingScrollRestore = null;
      this.restoreScrollAttempts = 0;
      return;
    }

    this.restoreScrollAttempts += 1;
    this.scheduleScrollRestore();
  }

  getSearchQueryKey(validQueryParams) {
    const { params: currentPathParams = {} } = this.props;
    const { listingType: listingTypePathParam } = currentPathParams;
    const { config } = this.props;

    return buildCanonicalSearchQueryKey({
      queryParams: validQueryParams,
      config,
      listingTypePathParam,
    });
  }

  onOpenFilterModal() {
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    this.setState({
      isFilterModalOpen: true,
      isMobileModalOpen: true,
      filterModalInitialQueryParams: urlQueryParams,
      currentQueryParams: urlQueryParams,
    });
  }

  onCloseFilterModal() {
    this.setState({
      isFilterModalOpen: false,
      isMobileModalOpen: false,
      filterModalInitialQueryParams: null,
    });
  }

  cancelFilterModal() {
    this.setState(prevState => ({
      currentQueryParams: prevState.filterModalInitialQueryParams || {},
    }));
    this.onCloseFilterModal();
  }

  applyFilterModal() {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const { sortConfig } = config?.search || {};
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const searchParams = this.state.currentQueryParams;
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, search));
    this.scrollToSearchTop();
    this.onCloseFilterModal();
  }

  handleKeywordSearch(keywords) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const { sortConfig } = config?.search || {};
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = {
      ...urlQueryParams,
      ...this.state.currentQueryParams,
      keywords: keywords || null,
    };
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    this.setState({ currentQueryParams: searchParams });
    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, search));
    this.scrollToSearchTop();
  }

  clearKeywordsOnly() {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const { sortConfig } = config?.search || {};
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const mergedQueryParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const currentKeywords = `${mergedQueryParams.keywords || ''}`.trim();
    if (!currentKeywords) {
      return;
    }

    const nextQueryParams = {
      ...mergedQueryParams,
      keywords: null,
      page: null,
    };
    const search = cleanSearchFromConflictingParams(nextQueryParams, filterConfigs, sortConfig);
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    this.setState({ currentQueryParams: nextQueryParams });
    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, search));
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, location } = this.props;

    // Reset state
    this.setState({
      currentQueryParams: {},
      filterModalInitialQueryParams: {},
    });

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, {}));
    this.scrollToSearchTop();
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { listingType: listingTypePathParam } = currentPathParams;
    const { sortConfig } = config?.search || {};
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);
    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
    this.scrollToSearchTop();
  }

  handleLoadMore({ hasNextPage, isFetchingNextPage, searchInProgress, currentPage, queryKey }) {
    const { onLoadMoreListings, location, config, params: currentPathParams = {} } = this.props;

    const guardReason = !hasNextPage
      ? 'no-next-page'
      : isFetchingNextPage
      ? 'fetch-in-progress-prop'
      : searchInProgress
      ? 'search-in-progress'
      : null;
    if (guardReason) {
      debugInfiniteScroll('load-more-skipped', {
        context: 'SearchPageWithGrid',
        reason: guardReason,
        currentPage: Number(currentPage || 1),
        nextPage: Number(currentPage || 1) + 1,
        hasMore: hasNextPage,
        isFetchingNextPage,
        searchInProgress,
        queryKey: queryKey || null,
        loadedCount: this.props.listings?.length || 0,
        totalItems: Number(this.props.pagination?.totalItems || 0),
      });
      return;
    }
    const nextPage = Number(currentPage || 1) + 1;
    debugInfiniteScroll('load-more-request', {
      context: 'SearchPageWithGrid',
      currentPage: Number(currentPage || 1),
      nextPage,
      queryKey: queryKey || null,
      hasMore: hasNextPage,
      isFetchingNextPage,
      searchInProgress,
      loadedCount: this.props.listings?.length || 0,
      totalItems: Number(this.props.pagination?.totalItems || 0),
      search: location.search,
    });

    return onLoadMoreListings({
      search: location.search,
      config,
      params: currentPathParams,
      page: nextPage,
      queryKey,
    })
      .then(response => {
        if (!response) {
          return response;
        }

        const responseListings = Array.isArray(response?.data?.data)
          ? response.data.data.length
          : 0;
        debugInfiniteScroll('load-more-success', {
          context: 'SearchPageWithGrid',
          requestedPage: nextPage,
          queryKey: queryKey || null,
          responseCount: responseListings,
          totalPages: Number(response?.data?.meta?.totalPages || 0),
          totalItems: Number(response?.data?.meta?.totalItems || 0),
        });
        return response;
      })
      .catch(error => {
        debugInfiniteScroll('load-more-failed', {
          context: 'SearchPageWithGrid',
          requestedPage: nextPage,
          queryKey: queryKey || null,
          status: error?.status || null,
          code: error?.data?.errors?.[0]?.code || null,
          message: error?.message || null,
        });
        throw error;
      });
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      currentPage = 0,
      hasNextPage = false,
      isFetchingNextPage = false,
      routeConfiguration,
      config,
      viewport,
      params: currentPathParams = {},
      currentUser,
    } = this.props;
    const params = new URLSearchParams(location.search);
    const isCostumesCategory = params.get('pub_category') === ListingCategory.COSTUMES;

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam } = currentPathParams;

    const { listingFields } = config?.listing || {};
    const { sortConfig } = config?.search || {};

    const { activeListingTypes } = getActiveListingTypes(config, listingTypePathParam);
    const defaultFiltersConfig = getDefaultFiltersConfig(config, listingTypePathParam);

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel', 'listingType'].includes(f.key)
    );
    // 1. Setup Built-in Filters
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));

    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );

    // 2. Separate Listing Fields
    const listingFieldsWithoutCostumes = listingFields.filter(
      field => !field.key.startsWith('costume')
    );

    const costumeListingFields = listingFields.filter(field =>
      ['costume', 'genreOption'].some(prefix => field?.key.startsWith(prefix))
    );

    // 3. Process Fields using your 'addOrUpdateField' logic
    // We initialize the array and loop through the correct source array based on category
    const resultFields = [];
    const fieldsToProcess = isCostumesCategory
      ? costumeListingFields
      : listingFieldsWithoutCostumes;

    fieldsToProcess.forEach(field => {
      addOrUpdateField(resultFields, field);
    });

    // 4. Filter the processed results
    // Remove specific keys from the normalized resultFields
    const filteredResultFields = resultFields.filter(
      field => !['listingTypeOption', 'genre', 'brandTypeOption', 'of'].includes(field.key)
    );
    // 5. Construct the Raw List
    // We combine all sources. Note that customSecondaryFilters might be added twice here
    // based on your original logic (once inside the ternary, once at the end).
    // We will fix the duplicates in the next step.
    const rawFiltersList = [
      // ...builtInPrimaryFilters,
      ...(isCostumesCategory
        ? [...filteredResultFields, ...customSecondaryFilters]
        : filteredResultFields),
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // 6. Deduplicate (The Fix)
    // We traverse the list and keep only the FIRST occurrence of each key.
    const uniqueFiltersMap = new Map();
    rawFiltersList.forEach(filter => {
      if (filter && filter.key && !uniqueFiltersMap.has(filter.key)) {
        uniqueFiltersMap.set(filter.key, filter);
      }
    });

    const availableFilters = Array.from(uniqueFiltersMap.values()).filter(
      f =>
        ![
          'unit_of_measurement',
          'costume_color',
          'brandTypeOption',
          'costume_brand',
          'costume_type',
          'costume_size',
          'listingTypeOption',
        ].includes(f.key)
    );
    // 7. Sort Result
    const desiredOrder = [
      'size',
      'brand',
      'color',
      'price',
      'condition',
      'type',
      'shank',
      'width',
      'genre',
    ];

    availableFilters.sort((a, b) => {
      const indexA = desiredOrder.indexOf(a.key);
      const indexB = desiredOrder.indexOf(b.key);
      // If both are not in the list (-1), order remains unchanged (0)
      // If one is in the list, it moves to the top
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    // Selected aka active filters
    const selectedFilters = getActiveFiltersFromQuery(searchParamsInURL, filterConfigs);
    const selectedFilterKeys = Object.keys(selectedFilters);
    const activeFilterCount = isKeywordSearch
      ? selectedFilterKeys.filter(key => key !== 'keywords').length
      : selectedFilterKeys.length;
    const hasAnyFilter = selectedFilterKeys.length > 0;
    const isSearchStateDirty = isSearchDirty({
      queryParams: validQueryParams,
      filterConfigs,
      sortConfig,
      isKeywordSearch,
    });
    const searchQueryKey = this.getSearchQueryKey(validQueryParams);

    const { totalItems, hasServerTotalItems } = getTotalItemsFromPagination({
      pagination,
      searchParamsAreInSync,
      loadedListingsCount: listings.length,
      context: 'SearchPageWithGrid',
    });
    const listingsAreLoaded = !searchInProgress && searchParamsAreInSync && hasServerTotalItems;

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Parse page heading to be included in the title
    const pageHeading = searchInProgress
      ? intl.formatMessage({ id: 'MainPanelHeader.loadingResults' })
      : intl.formatMessage({ id: 'MainPanelHeader.foundResults' }, { count: totalItems });

    // Plain links to the previous and next result pages. Shoppers get more results by scrolling,
    // but search engines only follow links, so without these they'd only see the first page.
    const linkPage = Number(pagination?.page || 1);
    const linkTotalPages = Number(pagination?.totalPages || 1);
    const pageHref = n =>
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        n > 1 ? { ...urlQueryParams, page: n } : urlQueryParams
      );
    const crawlPageLinks = listingsAreLoaded
      ? {
          prev: linkPage > 1 ? pageHref(linkPage - 1) : null,
          next: linkPage < linkTotalPages ? pageHref(linkPage + 1) : null,
        }
      : null;

    const { title, description, schema, heading } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      pageHeading
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        className={css.pageRoot}
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <LayoutSingleColumn
          hideMobileBottomNav={this.state.isFilterModalOpen}
          topbar={
            <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
          }
          footer={<FooterContainer />}
        >
          <div className={css.layoutWrapperContainer}>
            <div id="main-content" className={css.layoutWrapperMain} role="main">
              <div className={css.searchResultContainer}>
                <MainPanelHeader
                  heading={heading}
                  className={css.mainPanel}
                  sortByComponent={sortBy('desktop')}
                  isSortByActive={sortConfig.active}
                  listingsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  noResultsInfo={noResultsInfo}
                  onOpenFilterModal={this.onOpenFilterModal}
                  onClearAll={this.resetAll}
                  onClearKeywords={this.clearKeywordsOnly}
                  onSubmitSearch={this.handleKeywordSearch}
                  keywords={validQueryParams.keywords}
                  hasAnyFilter={hasAnyFilter}
                  isSearchDirty={isSearchStateDirty}
                  activeFilterCount={activeFilterCount}
                  filtersText={intl.formatMessage({ id: 'MainPanelHeader.filters' })}
                  clearText={intl.formatMessage({ id: 'MainPanelHeader.clear' })}
                  viewport={viewport}
                />
                <div
                  className={classNames(css.listingsForGridVariant, {
                    [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                  })}
                >
                  {searchListingsError ? (
                    <H3 className={css.error}>
                      <FormattedMessage id="SearchPage.searchError" />
                    </H3>
                  ) : null}
                  <SearchResultsPanel
                    className={css.searchListingsPanel}
                    listings={listings}
                    pagination={listingsAreLoaded ? pagination : null}
                    crawlPageLinks={crawlPageLinks}
                    isMapVariant={false}
                    onListingClick={this.saveScrollStateBeforeListingNavigation}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    searchInProgress={searchInProgress}
                    queryKey={searchQueryKey}
                    onLoadMore={() =>
                      this.handleLoadMore({
                        hasNextPage,
                        isFetchingNextPage,
                        searchInProgress,
                        currentPage,
                        queryKey: searchQueryKey,
                      })
                    }
                  />
                </div>
                <FilterModal
                  isOpen={this.state.isFilterModalOpen}
                  onClose={this.cancelFilterModal}
                  onApply={this.applyFilterModal}
                  onReset={this.resetAll}
                  onManageDisableScrolling={onManageDisableScrolling}
                  totalItems={totalItems}
                  viewportSafeLayout
                >
                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersModal.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    const filterId = `SearchFiltersModal.${filterConfig.key.toLowerCase()}`;

                    return (
                      <FilterComponent
                        key={key}
                        id={filterId}
                        className={css.filter}
                        config={filterConfig}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                      />
                    );
                  })}
                </FilterModal>
              </div>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    currentPage,
    hasNextPage,
    isFetchingNextPage,
  } = state.SearchPage;
  const listings = filterPublicListings(getListingsById(state, currentPageResultIds));

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    currentPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onLoadMoreListings: params => dispatch(loadMoreListings(params)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport
)(EnhancedSearchPage);

export default SearchPage;
